<template>
  <div  :class="inputClass" class="zs-input" 
       @mouseenter="isComponentHovered = true" 
       @mouseleave="isComponentHovered = false">
    <label :style="labelStyle" class="zsinput-label" v-if="label" :for="id">{{ label }}</label>
    <div data-tooltip class="input-holder">
      <div v-if="mask && showMask" :class="{
        small: size == 'small',
        medium: size == 'medium',
        big: size == 'big'
      }" class="ghost-input" aria-hidden="true">{{ ghostValue }}</div>
      <input @input="onInput" @keyup.enter="onEnterPress" @keyup.esc="onEscPress" :id="id" :class="{
        masked: mask != '',
        small: size == 'small',
        medium: size == 'medium',
        big: size == 'big',
        invalid: hasError,
        password: type == 'password'
      }" :type="internalType" :value="modelValue" :min="type === 'number' ? min : undefined" :max="type === 'number' ? max : undefined" :disabled="disabled" v-bind="$attrs" @focus="$emit('focus')" @blur="$emit('blur',modelValue)"
        ref="inputRef">

      <!-- <i v-if="type == 'password'" @click="togglePasswordVisibility" :class="{ 'fa-eye-slash': showPassword, 'fa-eye': !showPassword }" class="icon fa" /> -->
      <ZSIcon v-if="type == 'password'" color="#A0C1DE" :icon="showPassword ? 'Eye' : 'EyeSlash'" class="password-toggle" @click="togglePasswordVisibility" />


      <!-- <i v-if="clear && !busy && modelValue.length" @click="$emit('clear')" class="reset fa fa-delete-left" /> -->
      <ZSIcon v-if="busy" icon="Refresh" class="busy-icon" flip-h spin spin-direction="reverse" size="small" color="#A0C1DE"/>
      <ZSIcon v-else-if="clear && !busy && modelValue.length" @click="$emit('clear')" icon="Erase3" size="small" color="red" class="reset" />
      <ZSIcon v-else-if="type == 'dropdown'" icon="Chevron"  @click="$emit('drop')" class="icon" color="#A0C1DE" />
      <ZSIcon v-else-if="icon != ''" :icon="icon" class="icon" :color="iconColor" :size="iconSize" />

    </div>
    <small class="help-error" v-show="hasError && errorType === 'text'">{{
      validationResult.description ||
      error || internalError }}</small>
    
    <!-- Error Badge -->
    <div 
      v-if="shouldShowBadge" 
      :class="['error-badge', `error-badge-${errorBadgePosition}`, errorTooltipSide ? `tooltip-side-${errorTooltipSide}` : '']"
      @mouseenter="showTooltip = true"
      @mouseleave="showTooltip = false"
    >
      !
      <div 
        v-if="showTooltip" 
        class="error-tooltip"
      >
        {{ validationResult.description || error || internalError }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted } from 'vue'
// import ZSIcon from '@/components/zs-components/global/ZSIcon.vue'



const props = defineProps({
  label: { type: String, default: '' },
  type: { type: String, default: 'text' },
  modelValue: { type: [String, Number], default: '' }, // Changed from value to modelValue
  id: {
    type: String,
    default: () => `zs-input-${Math.random().toString(36).substr(2, 9)}`
  },
  name: { type: String, default: '' },
  valid: { type: [Boolean, String], default: true },
  toValidate: { type: Boolean, default: true },
  rules: { type: String, default: null },
  busy: { type: Boolean, default: false },
  clear: { type: Boolean, default: false },
  validateOnChange: { type: Boolean, default: true },
  error: { type: String, default: null },
  mask: { type: String, default: '' },
  showMask: { type: Boolean, default: false },
  size: { type: String, default: 'small' },
  inputClass: { type: String, default: '' },
  labelColor: { type: String, default: '' },
  labelSize: { type: String, default: 'small' },
  icon: { type: String, default: '' },
  iconColor: { type: String, default: '' },
  iconSize: { type: String, default: '' },
  errorType: { type: String, default: 'badge' },
  errorBadgePosition: { type: String, default: 'br' },
  errorBadgeOnHover: { type: Boolean, default: false },
  errorTooltipSide: { type: String, default: '' },
  field:{type:String,default:''},
  min: { type: Number, default: null },
  max: { type: Number, default: null },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'validation', 'focus', 'blur', 'clear', 'input', 'enter', 'esc','drop'])

const validator = inject('validator', null)

const showPassword = ref(false)
const internalType = ref(props.type)
const internalValue = ref(props.modelValue)
const validationResult = ref({})
const ghostValue = ref('')
const inputRef = ref(null)
const internalError = ref('')
const showTooltip = ref(false)
const isComponentHovered = ref(false)

const smallClass = computed(() => ({ hidden: false }))

const hasError = computed(() => {
  return validationResult.value && validationResult.value.description || props.error || internalError.value
})

const shouldShowBadge = computed(() => {
  if (!hasError.value || props.errorType !== 'badge') return false
  if (props.errorBadgeOnHover) {
    return isComponentHovered.value
  }
  return true
})

const labelStyle = computed(() => {
  let r = {}
  if (props.labelColor) {
    r = { color: props.labelColor }
  }

  if (props.labelSize) {
    r.fontSize = props.labelSize
  }

  return r
})

const isMaskCharacter = (char) => ["L", "D", "A", "?", "#", "*"].includes(char)

const isValidCharacter = (maskChar, inputChar) => {
  switch (maskChar) {
    case "L": return /[a-zA-Z]/i.test(inputChar)
    case "#": return /\d/.test(inputChar)
    case "A": return /[a-zA-Z0-9]/i.test(inputChar)
    case "*": return true
    case "?": return /[a-zA-Z0-9\s]/i.test(inputChar)
    default: return false
  }
}

const formatInput = (value) => {
  if (!props.mask) return value
  let formattedValue = ""
  let inputIndex = 0
  let maskIndex = 0

  while (maskIndex < props.mask.length && inputIndex < value.length) {
    const maskChar = props.mask[maskIndex]
    const inputChar = value[inputIndex]
    if (isMaskCharacter(maskChar)) {
      if (isValidCharacter(maskChar, inputChar)) {
        formattedValue += inputChar
        maskIndex++
      }
      inputIndex++
    } else {
      formattedValue += maskChar
      maskIndex++
    }
  }

  return formattedValue
}

const removeTrailingMaskCharacters = (value) => {
  if (!props.mask || value.length == props.mask.length || isMaskCharacter(value[value.length - 1]))
    return value
  let newValue = value
  for (let i = value.length - 1; i >= 0; i--) {
    if (isMaskCharacter(props.mask[i])) {
      break
    }
    newValue = newValue.slice(0, -1)
  }
  return newValue
}

const updateGhostValue = () => {

  if (!props.mask) return
  let ghost = ''
  let valueIndex = 0
  const mask = props.mask.replace(/[LDA#?*]/g, "_")


  for (let i = 0; i < props.mask.length; i++) {
    if (valueIndex < internalValue.value.length) {
      ghost += internalValue.value[valueIndex]
      valueIndex++
    } else {
      ghost += mask[i]
    }
  }

  ghostValue.value = ghost
}

const validateData = (value) => {
  if (validator && props.rules != null && props.rules !== '') {
    validationResult.value = validator.validate({
      id: props.id,
      value: value,
      rules: props.rules,
      field:props.field || props.label,
      internalError:internalError.value
    })

    emit('validation', validationResult.value)
    return validationResult.value
  } else if(props.rules == ''){
    validationResult.value = validator.validate({
      id: props.id,
      value: value,
      rules: '',
      field:props.field || props.label,
      internalError:internalError.value
    })
    emit('validation', validationResult.value)
    return validationResult.value    
  }
  return null
}

const validateNumberRange = (value) => {
  if (props.type === 'number' && value !== '') {
    const numValue = Number(value)
    
    if (isNaN(numValue)) {
      return value
    }
    
    let constrainedValue = numValue
    
    if (props.min !== null && numValue < props.min) {
      constrainedValue = props.min
    }
    
    if (props.max !== null && numValue > props.max) {
      constrainedValue = props.max
    }
    
    return constrainedValue.toString()
  }
  
  return value
}

const onInput = (event) => {

  const ori = formatInput(event.target.value)
  let val = removeTrailingMaskCharacters(ori)
  
  // Apply number range validation for number inputs
  val = validateNumberRange(val)

  if (props.validateOnChange) validateData(val)
  event.target.value = val
  emit('input', val)
  emit('update:modelValue', val) // Changed from 'input' to 'update:modelValue'
}

const onEnterPress = () => {
  emit('enter', internalValue.value)
}

const onEscPress = () => {
  emit('esc', internalValue.value)
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
  internalType.value = showPassword.value ? 'text' : 'password'
}

const reset = () => {
  validationResult.value = {}
  internalError.value = ''
}

const setError = (errorMessage) => {
  internalError.value = errorMessage
}

const clearError = () => {
  internalError.value = ''
}

const validate = () => {
  return validateData(internalValue.value)
}

const subscribeToValidator = () => {
  if (validator) {
    validator.subscribe({ validate, reset })
  }
}

// Update the watch to use modelValue instead of value
watch(() => props.modelValue, (newValue) => {
  let formattedValue = formatInput(newValue)
  formattedValue = validateNumberRange(formattedValue)
  internalValue.value = formattedValue
  if (props.validateOnChange) validateData(newValue)
  updateGhostValue()
  if (formattedValue !== newValue) {
    emit('update:modelValue', internalValue.value)
  }
})

watch(() => props.rules, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    validateData(internalValue.value)
  }
})

onMounted(() => {
  if (props.toValidate) subscribeToValidator()
  updateGhostValue()
  // if (props.validateOnChange) validateData(internalValue.value)
})

defineExpose({
  validate,
  reset,
  setError,
  clearError,
  getRef: () => inputRef.value
})
</script>

<style>
.zs-input {
  /* CSS Variables for ZSInput Component */
  /* Input Base */
  --zs-input-font-family: "Poppins";
  --zs-input-width: 100%;
  --zs-input-color: #21425F;
  /* var(--color-menu) */
  --zs-input-bg-color: white;
  /* var(--color-input-bg) */
  --zs-input-border: 1px solid #a0c1de;
  /* var(--color-tab) */
  --zs-input-outline: none;

  /* Input Label */
  --zs-input-label-font-size: 12px;
  --zs-input-label-font-family: "Poppins";
  --zs-input-label-color: #21425F;
  /* var(--color-menu) */
  --zs-input-label-margin-right: 30px;
  --zs-input-label-padding-bottom: 5px;

  /* Input Error */
  --zs-input-error-color: rgb(243, 100, 100);
  /* var(--color-input-error) */
  --zs-input-error-font-size: 12px;
  --zs-input-invalid-border-color: rgb(252, 0, 0);
  /* var(--color-input-invalid-border) */

  /* Input Sizes - Small */
  --zs-input-small-height: 32px;
  --zs-input-small-font-size: 12px;
  --zs-input-small-padding: 0 0 0 12px;
  --zs-input-small-border-radius: 4px;

  /* Input Sizes - Medium (Default) */
  --zs-input-medium-height: 40px;
  --zs-input-medium-font-size: 14px;
  --zs-input-medium-padding: 0 0 0 15px;
  --zs-input-medium-border-radius: 5px;

  /* Input Sizes - Big */
  --zs-input-big-height: 50px;
  --zs-input-big-font-size: 16px;
  --zs-input-big-padding: 0 0 0 15px;
  --zs-input-big-border-radius: 10px;
  --zs-input-big-font-weight: 400;

  /* Input States */
  --zs-input-disabled-bg-color: #f5f5f5;
  /* var(--color-input-disabled-bg) */
  --zs-input-disabled-border: 1px solid transparent;
  /* var(--color-input-disabled-border) */
  --zs-input-readonly-bg-color: inherit;

  /* Input Icon */
  --zs-input-icon-font-size: 80%;
  --zs-input-icon-right: 10px;
  --zs-input-icon-color: #999990;
  /* var(--color-input-icon) */

  /* Input Search */
  --zs-input-search-bg-color: #f5f5f5;
  /* var(--color-input-search-bg) */

  /* Ghost Input for Mask */
  --zs-input-ghost-color: #666;
  /* var(--color-input-ghost) */
  --zs-input-ghost-left: 1px;

  /* Help Text */
  --zs-input-help-color: #999990;
  /* var(--color-input-help) */
  --zs-input-help-font-size: 12px;
  --zs-input-help-margin-top: 5px;

  position: relative;

  .help-error {
    color: var(--zs-input-error-color);
    font-size: var(--zs-input-error-font-size);
    position: absolute;
    height: 20px;
    left: 0px;
    bottom: -20px;
  }

  .error-badge {
    position: absolute;
    width: 14px;
    height: 14px;
    background-color: var(--zs-input-error-color);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: bold;
    z-index: 10;
    cursor: pointer;
    
    .error-tooltip {
      position: absolute;
      background-color: rgb(243, 100, 100);
      color: white;
      padding: 6px 9px;
      border-radius: 4px;
      font-size: px;
      font-weight: normal;
      white-space: nowrap;
      z-index: 1000;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      
      /* Position tooltip to the right by default */
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      
      /* Arrow pointing to the badge */
      &::before {
        content: '';
        width: 1px;
        height: 1px;
        /* background-color: green; */
        position: absolute;
        left: -5px;
        top: 50%;
        /* transform: translateY(-50%); */
        border: 15px solid transparent;
        border-right-color: rgb(243, 100, 100);
      }
    }
    
    /* Position classes */
    &.error-badge-tl {
      top: 18px;
      left: -6px;
      
      .error-tooltip {
        left: 25px;
        top: 0;
        transform: none;
      }
    }
    
    &.error-badge-t {
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      
      .error-tooltip {
        left: 50%;
        top: 25px;
        transform: translateX(-50%);
        
        &::before {
          left: 50%;
          top: -5px;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-bottom-color: rgb(243, 100, 100);
        }
      }
    }
    
    &.error-badge-tr {
      bottom: 26px;
      right: -4px;
      
      .error-tooltip {
        right: 10px;
        top: 0;
        left: auto;
        transform: none;
        
        &::before {
          right: -5px;
          left: auto;
          border: 5px solid transparent;
          border-left-color: rgb(243, 100, 100);
        }
      }
    }
    
    &.error-badge-r {
      top: 50%;
      right: -9px;
      transform: translateY(-50%);
      
      .error-tooltip {
        right: 25px;
        left: auto;
        
        &::before {
          right: -5px;
          left: auto;
          border: 5px solid transparent;
          border-left-color: rgb(243, 100, 100);
        }
      }
    }
    
    &.error-badge-br {
      bottom: 9px;
      right:9px;

      .error-tooltip {
        right: -5px;
        bottom: -40px;
        top: auto;
        left: auto;
        transform: none;
        
        &::before {
          right: 0;
          left: auto;
          top: -65%;
          /* transform: translateY(-50%); */
          border: 10px solid transparent;
          border-bottom-color: rgb(243, 100, 100);
        }
      }
    }
    
    &.error-badge-b {
      bottom: -9px;
      left: 50%;
      transform: translateX(-50%);
      
      .error-tooltip {
        left: 50%;
        bottom: 25px;
        top: auto;
        transform: translateX(-50%);
        
        &::before {
          left: 50%;
          bottom: -5px;
          top: auto;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: rgb(243, 100, 100);
        }
      }
    }
    
    &.error-badge-bl {
      bottom: -9px;
      left: -9px;
      
      .error-tooltip {
        left: 25px;
        bottom: 0;
        top: auto;
        transform: none;
        
        &::before {
          left: -5px;
          top: 50%;
          bottom: auto;
          transform: translateY(-50%);
          border: 5px solid transparent;
          border-right-color: rgb(243, 100, 100);
        }
      }
    }
    
    &.error-badge-l {
      top: 50%;
      left: -9px;
      transform: translateY(-50%);

      .error-tooltip {
        left: 25px;

        &::before {
          left: -5px;
          border: 5px solid transparent;
          border-right-color: rgb(243, 100, 100);
        }
      }
    }

    /* Tooltip side overrides — flip tooltip direction independently of badge position */
    &.tooltip-side-right .error-tooltip {
      left: -5px;
      right: auto;

      &::before {
        left: 0;
        right: auto;
      }
    }

    &.tooltip-side-left .error-tooltip {
      right: -5px;
      left: auto;

      &::before {
        right: 0;
        left: auto;
      }
    }

    &.tooltip-side-top .error-tooltip {
      bottom: 25px;
      top: auto;
      left: 50%;
      right: auto;
      transform: translateX(-50%);

      &::before {
        bottom: -5px;
        top: auto;
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        border: 10px solid transparent;
        border-top-color: rgb(243, 100, 100);
        border-bottom-color: transparent;
      }
    }

    &.tooltip-side-bottom .error-tooltip {
      top: 25px;
      bottom: auto;
      left: 50%;
      right: auto;
      transform: translateX(-50%);

      &::before {
        top: -5px;
        bottom: auto;
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        border: 10px solid transparent;
        border-bottom-color: rgb(243, 100, 100);
        border-top-color: transparent;
      }
    }
  }


label.zsinput-label {
  font-size: 1em;
  font-family: var(--zs-input-label-font-family);
  color: rgb(191, 190, 190);
  text-align: left;
  margin-right: var(--zs-input-label-margin-right);
  padding-bottom: var(--zs-input-label-padding-bottom);
  display: block;
  white-space: nowrap;
}

.input-holder {
  display: flex;
  align-items: center;
  position: relative;

  .icon {
    font-size: var(--zs-input-icon-font-size);
    position: absolute;
    right: var(--zs-input-icon-right);
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: var(--zs-input-icon-color);
  }

  .busy {
    font-size: var(--zs-input-icon-font-size);
    position: absolute;
    right: var(--zs-input-icon-right);
    top: 30%;
    transform: translateY(-100%);
    cursor: pointer;
    color: var(--zs-input-icon-color);
  }

  .reset {
    font-size: var(--zs-input-icon-font-size);
    position: absolute;
    right: var(--zs-input-icon-right);
    top: 19%;
    cursor: pointer;
    color: var(--zs-input-icon-color);
    background-color: transparent;
  }

  .password-toggle {
    position: absolute;
    right: var(--zs-input-icon-right);
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
  }
}

.input-holder:has(.small){
  .reset{
    top:15%
  }
}
.input-holder:has(.big){
  .reset{
    top:25%
  }
}

.busy-icon {
  position: absolute;
  right:10px;
}


input {
  height: var(--zs-input-medium-height);
  width: var(--zs-input-width);
  border: var(--zs-input-border);
  background-color: var(--zs-input-bg-color);
  border-radius: var(--zs-input-medium-border-radius);
  padding: var(--zs-input-medium-padding);
  color: var(--zs-input-color);
  font-family: var(--zs-input-font-family);
  font-size: var(--zs-input-medium-font-size);
  outline: var(--zs-input-outline);

  &.invalid {
    border-color: var(--zs-input-invalid-border-color);
  }

  &[readonly] {
    background-color: var(--zs-input-readonly-bg-color);
    cursor: default;
  }

  &:disabled {
    background-color: var(--zs-input-disabled-bg-color);
    cursor: not-allowed;
    border: var(--zs-input-disabled-border);
  }

  &.masked {
    background-color: transparent;
  }

  &.full-width {
    width: 100%;
  }

  /* Small size input */
  &.small {
    height: var(--zs-input-small-height);
    font-size: var(--zs-input-small-font-size);
    padding: var(--zs-input-small-padding);
    border-radius: var(--zs-input-small-border-radius);
  }

  /* Medium size input (default) */
  &.medium {
    height: var(--zs-input-medium-height);
    font-size: var(--zs-input-medium-font-size);
    padding: var(--zs-input-medium-padding);
    border-radius: var(--zs-input-medium-border-radius);
  }

  /* Big size input */
  &.big {
    width: var(--zs-input-width);
    /* border: none; */
    border-radius: var(--zs-input-big-border-radius);
    font-size: var(--zs-input-big-font-size);
    height: var(--zs-input-big-height);
    padding: var(--zs-input-big-padding);
    font-weight: var(--zs-input-big-font-weight);
  }

  &.search {
    width: var(--zs-input-width);
    border-radius: var(--zs-input-big-border-radius);
    font-size: var(--zs-input-big-font-size);
    height: var(--zs-input-big-height);
    padding: var(--zs-input-big-padding);
    font-weight: var(--zs-input-big-font-weight);
    background-color: var(--zs-input-search-bg-color);
  }

  &.password {
    padding-right: 30px;
  }
}

.ghost-input {
  position: absolute;
  left: var(--zs-input-ghost-left);
  color: var(--zs-input-ghost-color);
  font-family: var(--zs-input-font-family);
  font-size: var(--zs-input-medium-font-size);
  border: none;
  background-color: transparent;
  padding: 0px 15px;
  white-space: nowrap;
  pointer-events: none;
}

/* Add size-specific ghost input styles */
.ghost-input.small {
  font-size: var(--zs-input-small-font-size);
  padding: 0px 12px;
}

.ghost-input.big {
  font-size: var(--zs-input-big-font-size);
  padding: 0px 15px;
}

small {
  color: var(--zs-input-help-color);
  font-size: var(--zs-input-help-font-size);
  margin-top: var(--zs-input-help-margin-top);

  &.hidden {
    user-select: none;
    opacity: 0;
  }
}
}
</style>
